import React, {useState, useEffect} from "react"

import {RenewalQuoteStatus, ZuoraCurrentPlan, ZuoraProduct, CancellationReason, RenewalProduct} from "@irati/types"
import buService from "@irati/billing/services/bu"
import authentication from "@irati/auth"
import {IconTypes} from "@irati/ui/src/Icon"
import {InjectIntl, injectIntl} from "@irati/utils/lang"
import FormatMessage from "@irati/utils/lang/types/FormatMessage"
import adminTracking, {RenewalPlanType, RenewalPlanPage, RenewalPlanSubSource, RenewalPlanSource} from "@irati-tracking/admin/admin"

import {AddOns} from "../../Renewal/Header"
import RenewalsProgressBanner from "../../Renewal/RenewalsProgressBanner"
import {FLEXI_POINTS_PREFIX} from "../../Billing/RenewalQuoteSelection/constants"
import ChangePlanModal from "./ChangePlanModal"
import CancellationModal from "./CancellationModal"
import WinbackModal from "./WinbackModal"
import FinalCancellationModal from "./FinalCancellationModal"
import CurrentPlanInfo from "./CurrentPlanInfo"

export const getAddOns = (products: ZuoraProduct[] | RenewalProduct[], seats: number | undefined, formatMessage: FormatMessage) => {
  const addOns = products?.map((product: ZuoraProduct | RenewalProduct) => {
    const hasFlexiPoints = product.name?.toLowerCase().includes(FLEXI_POINTS_PREFIX)
    return {
      icon: hasFlexiPoints ? IconTypes.flexi : IconTypes.multiComments,
      label: product.name || ""
    }
  })
  return [...[{icon: IconTypes.userPlus, label: formatMessage({id: "admin.billing.renewals.pills.users"}, {seats})}], ...addOns]
}

export interface CancellationInput {
  reason?: CancellationReason
  reasonText?: string
}

interface ModalFlow {
  changeModal?: boolean
  cancellationModal?: boolean
  winbackModal?: boolean
  finalCancellationModal?: boolean
}

export type CurrentPlanCallback = (updatedSubSource: RenewalPlanSubSource) => void

interface Props extends InjectIntl {
  status?: RenewalQuoteStatus
  isManual: boolean
  onCallback: CurrentPlanCallback
  onTimeout: () => void
  onCancel: () => void
  isRenewalWindowOpen?: boolean
  source: RenewalPlanSource
}

const CurrentPlan: React.FC<Props> = ({status, isManual, onCallback, onCancel, isRenewalWindowOpen, onTimeout, source, formatMessage}) => {
  const [cancellationReason, setCancellationReason] = useState<CancellationInput>()
  const [renewalStatus, setRenewalStatus] = useState<RenewalQuoteStatus | undefined>(status)
  const [currentPlan, setCurrentPlan] = useState<ZuoraCurrentPlan>()
  const [addOns, setAddOns] = useState<AddOns[]>([])

  const [modalFlow, setModalFlow] = useState<ModalFlow>({
    changeModal: false,
    cancellationModal: false,
    winbackModal: false,
    finalCancellationModal: false
  })

  const handleCancellationNext = (reason?: CancellationReason, reasonText?: string) => {
    setCancellationReason({reason, reasonText})
    setModalFlow({cancellationModal: false, winbackModal: true})
  }

  const handleFinalCancellation = () => {
    setModalFlow({finalCancellationModal: false})
    setRenewalStatus(RenewalQuoteStatus.CancellationCreated)
    onCancel()
  }

  useEffect(() => {
    buService.getCurrentPlan({businessId: authentication.business().uuid!}).then((data) => {
      setAddOns(getAddOns(data?.addOns, data?.seats, formatMessage))
      setCurrentPlan(data)
    })
  }, [])

  useEffect(() => {
    setRenewalStatus(status)
  }, [status])

  return (
    <>
      {isRenewalWindowOpen &&
        (status === RenewalQuoteStatus.Available ||
          status === RenewalQuoteStatus.SentForSignature ||
          status === RenewalQuoteStatus.SignedInProgress) && (
          <RenewalsProgressBanner activeStep={status === RenewalQuoteStatus.Available ? 1 : 3} />
        )}
      <CurrentPlanInfo
        isRenewalWindowOpen={isRenewalWindowOpen}
        isManual={isManual}
        currentPlan={currentPlan}
        addOns={addOns}
        renewalStatus={renewalStatus}
        onCallback={onCallback}
        onChangeModal={() => setModalFlow({changeModal: true})}
      />
      <ChangePlanModal
        show={!!modalFlow.changeModal}
        renewalWindow={currentPlan?.window}
        renewalStatus={renewalStatus}
        onHide={(ignoreTracking: boolean) => {
          if (!ignoreTracking) {
            adminTracking.renewalPlan({
              stage: 2,
              type: RenewalPlanType.changePlanFlow,
              page: RenewalPlanPage.changeplanStage,
              action: "hideChangeModal",
              source
            })
          }
          setModalFlow({changeModal: false})
        }}
        onCancel={() => setModalFlow({cancellationModal: true})}
        source={source}
      />
      <CancellationModal
        show={!!modalFlow.cancellationModal}
        renewalWindow={currentPlan?.window}
        onHide={(ignoreTracking: boolean) => {
          if (!ignoreTracking) {
            adminTracking.renewalPlan({
              stage: 3,
              type: RenewalPlanType.cancellationFlow,
              action: "hideCancellationModal",
              source
            })
          }
          setModalFlow({cancellationModal: false})
        }}
        onPrevious={() => setModalFlow({changeModal: true, cancellationModal: false})}
        onNext={handleCancellationNext}
        source={source}
        endDate={currentPlan?.endDate}
      />
      <WinbackModal
        show={!!modalFlow.winbackModal}
        cancellation={cancellationReason}
        onHide={(ignoreTracking: boolean) => {
          if (!ignoreTracking) {
            adminTracking.renewalPlan({
              stage: 4,
              type: RenewalPlanType.cancellationFlow,
              action: "hideWinbackModal",
              source
            })
          }
          setModalFlow({winbackModal: false})
        }}
        onPrevious={() => setModalFlow({winbackModal: false, cancellationModal: true})}
        onNext={() => setModalFlow({winbackModal: false, finalCancellationModal: true})}
        onTimeout={onTimeout}
        addOns={addOns}
        source={source}
      />
      <FinalCancellationModal show={!!modalFlow.finalCancellationModal} addOns={addOns} onHide={handleFinalCancellation} />
    </>
  )
}

export default injectIntl(CurrentPlan)
